import React from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import { captureError } from './services/sentry';
import { TranslationProvider } from './i18n/TranslationContext';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider } from './contexts/AuthContext';
import { ScrollToTop } from './components/ScrollToTop';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { HelpPage } from './pages/HelpPage';
import { TermsPage } from './pages/TermsPage';
import { NewUiEntry } from './newui/NewUiEntry';
import { PublicLanding } from './newui/PublicLanding';
import { AuthScreen } from './newui/screens';
import { V2_BASE, v2Path } from './newui/nav';

/**
 * Every data failure in the app already throws — api/client.ts has ~90 call
 * sites that throw on a non-ok response — but until now those errors died in a
 * toast. Reporting them here catches all of them at once, with the query key
 * as context, instead of instrumenting each call site.
 *
 * Queries are reported only after retries are exhausted, so a request that
 * recovers on its second attempt is not an issue.
 */
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureError(error, {
        where: 'react-query',
        kind: 'query',
        queryKey: JSON.stringify(query.queryKey),
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Mutations are user-initiated writes — a transfer, a listing edit, a
      // card add. A failure here is something the user was actively trying to
      // do, so it matters more than a background refetch.
      captureError(error, {
        where: 'react-query',
        kind: 'mutation',
        mutationKey: JSON.stringify(mutation.options.mutationKey ?? null),
      });
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // One retry for the transient stuff, none for an auth refusal: a 401 or
      // 403 is the same answer on the second ask, and retrying it turned one
      // expired admin session into fifty Sentry events in three minutes.
      // A 429 is the same story with the opposite cure: the API is asking us
      // to send less, and the retry is one more request against a bucket that
      // is already empty (2026-09-18: one throttled account produced ~550
      // requests in three minutes, every one of them a 429).
      retry: (failureCount, error) => !isAuthRefusal(error) && failureCount < 1,
    },
  },
});

/**
 * Whether a query failed because the API refused the caller (401/403) or
 * throttled them (429), not because of a transient fault. Most callers throw
 * a bare `Error('Failed to X: 403')`, so the status is read off the message
 * when it was not attached as a field.
 */
function isAuthRefusal(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  if (status === 401 || status === 403 || status === 429) return true;
  const message = error instanceof Error ? error.message : '';
  return /\b(40[13]|429)$/.test(message);
}

/**
 * Switchover shim: v2 is the shipped UI, and every legacy v1 path (bookmarks,
 * Stripe return URLs, admin-panel redirects, emailed links) is forwarded to its
 * /v2/app twin with query and hash intact. Paths v2Path() cannot map fall back
 * to the shell root rather than 404ing. The v1 pages under src/pages/ are no
 * longer mounted; revert this file to restore them.
 */
function LegacyRedirect() {
  const location = useLocation();
  const target = v2Path(location.pathname + location.search + location.hash);
  return <Navigate to={target.startsWith(V2_BASE) ? target : V2_BASE} replace />;
}

function AppContent() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<AuthScreen />} />
        <Route path="/admin-login" element={<AdminLoginPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/v2/*" element={<NewUiEntry />} />
        {/* Public front door. Must sit above the /* catch-all so signed-out
            visitors get marketing copy rather than a login bounce. */}
        <Route path="/" element={<PublicLanding />} />
        <Route path="/*" element={<LegacyRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * Last-resort fallback when a render throws. Sentry has already captured the
 * error by the time this paints; a reload is the only useful recovery.
 */
function CrashFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
        <p className="mt-2 text-sm text-gray-600">
          We've been notified and are looking into it. Reloading usually fixes it.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-6 rounded-lg bg-black px-5 py-2.5 text-sm font-medium text-white"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<CrashFallback />}>
      <QueryClientProvider client={queryClient}>
        <TranslationProvider>
          <ToastProvider>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </ToastProvider>
        </TranslationProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
